<template>
  <div
    id="autocomplete-dropdown"
    class="autocomplete-dropdown"
    data-cnstrc-autosuggest
  >
    <div class="autocomplete-wrapper">
      <div
        class="suggestion-container"
        :class="suggestionsClass"
      >
        <div
          v-if="getRecentlySearched().length === 3 && !searchTerm.length"
          id="recently_searched"
          class="recently_searched"
        >
          <p class="title">
            Recently Searched
          </p>
          <ul class="listbox recent">
            <li
              v-for="suggestion in getRecentlySearched()"
              :key="suggestion"
              class="option"
            >
              <a
                :href="getSearchSuggestionURL(suggestion)"
                class="suggestion"
                @click="recentHandler(suggestion)"
              >
                {{ suggestion }}
              </a>
            </li>
          </ul>

          <p class="title trending">
            Trending Searches
          </p>
          <ul
            id="trending-searches"
            class="listbox trending"
            role="listbox"
            v-bind="recommenderTrackingAttributesForTermsContainer"
          >
            <li
              v-for="(suggestion, index) in termsWithAttributes"
              :id="`trending-searches-${index}`"
              :key="suggestion.data.id"
              :aria-selected="selectedElement === `trending-searches-${index}`"
              class="option"
              role="option"
              v-bind="suggestion.attrs"
            >
              <a
                :href="getSearchSuggestionURL(suggestion.value)"
                class="suggestion"
                @click="trendingHandler(suggestion.value)"
              >
                {{ suggestion.value }}

                <span class="count">
                  {{ suggestion.data.total_num_results }}
                </span>
              </a>
            </li>
          </ul>
        </div>

        <div
          v-if="isSkuSearch(searchTerm)"
          id="no-results-term"
        >
          <a
            :href="noResultsLink()"
          >
            {{ products[0].data.id }} -
            <strong>
              {{ products[0].data.brand_collection }} {{ products[0].value }}
            </strong>
          </a>
        </div>

        <div
          v-else-if="getSuggests().length && getRecentlySearched().length < 3 || getSuggests().length && searchTerm.length"
          id="autocomplete"
        >
          <p
            v-if="suggested"
            class="title"
          >
            Trending Searches
          </p>
          <ul
            id="trending-searches"
            class="listbox"
            role="listbox"
            v-bind="recommenderTrackingAttributesForTermsContainer"
          >
            <li
              v-for="(suggestion, index) in termsWithAttributes"
              :id="`trending-searches-${index}`"
              :key="suggestion.data.id"
              v-bind="suggestion.attrs"
              ref="options"
              :aria-selected="selectedElement === `trending-searches-${index}`"
              class="option"
              role="option"
            >
              <a
                :href="getSearchSuggestionURL(suggestion.value)"
                class="suggestion"
                @click="trendingHandler(suggestion.value)"
              >
                {{ suggestion.value }}
                <span class="count">
                  {{ suggestion.data.total_num_results }}
                </span>
              </a>
            </li>
          </ul>
        </div>

        <div
          v-else-if="searchTerm.length && !(suggestions.length)"
          id="no-results-term"
        >
          <p v-if="products.length !== 1 || searchTerm.length < 3">
            Search for
          </p>
          <a
            :href="noResultsLink()"
          >
            <template v-if="isSkuSearch(searchTerm)">
              {{ products[0].data.id }} -
              <strong>
                {{ products[0].data.brand_collection }} {{ products[0].value }}
              </strong>
            </template>
            <template v-else>
              {{ searchTerm }}
            </template>
          </a>
        </div>
      </div>

      <div
        v-if="productsWithAttributes.length"
        id="products"
      >
        <span class="section-title">{{ getHeading() }}</span>
        <ul
          class="listbox"
          role="listbox"
          v-bind="recommenderTrackingAttributesForProductContainer"
        >
          <li
            v-for="product in productsWithAttributes"
            :key="product.data.id"
            v-bind="product.attrs"
            class="option"
          >
            <Product
              :product="product"
              @product-click="productHandler"
            />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import Product from './Product.vue';

export default {
  name: "Autocomplete",
  components: {
    Product
  },
  props: {
    products: {
      type: Array,
      required: true
    },
    responseTopProducts: {
      type: Object,
      require: false,
      default: undefined
    },
    responseTopSuggestions: {
      type: Object,
      require: false,
      default: undefined
    },
    searchTerm: {
      type: String,
      required: false,
      default: undefined
    },
    selectedElement: {
      type: String,
      required: false,
      default: undefined
    },
    suggestions: {
      type: Array,
      required: true
    },
    suggestionsClass: {
      type: String,
      required: false,
      default: undefined
    }
  },
  emits: [
    'recentClick',
    'suggestionClick',
    'productClick'
  ],
  computed: {
    suggested() {
      // Return true if suggest value is a recommended suggestion
      return this.getSuggests().length && !this.getRecentlySearched().length < 3 && !this.searchTerm.length;
    },
    recommenderTrackingAttributesForTermsContainer() {
      let attrs = {};

      if (this.searchTerm.length) {
        return attrs;
      }

      if (! this.responseTopSuggestions?.response) {
        return attrs;
      }

      // https://docs.constructor.io/integration/tracking/#recommendations
      attrs['data-cnstrc-recommendations'] = '';
      attrs['data-cnstrc-recommendations-pod-id'] = this.responseTopSuggestions.response.pod.id;
      attrs['data-cnstrc-result-id'] = this.responseTopSuggestions.result_id;
      attrs['data-cnstrc-num-results'] = this.suggestions.length;

      return attrs;
    },
    termsWithAttributes() {
      let config = this.getConfig(),
          length,
          optionKey = 'withRecent',
          sectionKey = this.isMobile() ? 'mobile' : 'desktop';

      if (this.searchTerm.length || !this.searchTerm.length && this.getRecentlySearched().length < 3) {
        optionKey = 'search';
      }

      length = config[sectionKey][optionKey];

      return this.suggestions.slice(0, +length).map((t) => {
        let attrs = {};

        attrs['data-cnstrc-item-name'] = t.value;

        // Tracking data for Autosuggest
        // https://docs.constructor.io/integration/tracking/#autosuggest
        if (this.searchTerm.length) {
          attrs['data-cnstrc-item-section'] = 'Search Suggestions';
        }

        // Tracking data for Recommendation
        // https://docs.constructor.io/integration/tracking/#recommendations
        if (! this.searchTerm.length) {
          attrs['data-cnstrc-item'] = 'Recommendation';
          // Not every term has a strategy
          if (t.strategy) {
            attrs['data-cnstrc-strategy-id'] = t.strategy?.id;
          }
          attrs['data-cnstrc-item-id'] = t.data.id;
        }

        t.attrs = attrs;

        return t;
      });
    },
    productsWithAttributes() {
      let config = this.getConfig(),
          sectionKey = this.isMobile() ? 'mobile' : 'desktop',
          length = config[sectionKey].visual;

      return this.products.slice(0, +length).map((p) => {
        let attrs = {
          'role': 'option'
        };

        attrs['data-cnstrc-item-name'] = p.value;
        attrs['data-cnstrc-item-id'] = p.data.id;

        // Tracking data for Autosuggest
        // https://docs.constructor.io/integration/tracking/#autosuggest
        if (this.searchTerm.length) {
          attrs['data-cnstrc-item-section'] = 'Products';
        }

        // Tracking data for Recommendation
        // https://docs.constructor.io/integration/tracking/#recommendations
        if (! this.searchTerm.length) {
          attrs['data-cnstrc-item'] = 'Recommendation';
          // Not ever product has a strategy
          if (p.strategy) {
            attrs['data-cnstrc-strategy-id'] = p.strategy?.id;
          }
        }

        p.attrs = attrs;

        return p;
      });
    },
    recommenderTrackingAttributesForProductContainer() {
      // If there is a search term it is displaying Autosuggest results
      // Autosuggest doesn't have tracking attributes on the result's
      // immediate parent.
      if (this.searchTerm.length) {
        return null;
      }

      if (! this.responseTopProducts?.response) {
        return null;
      }

      // https://docs.constructor.io/integration/tracking/#recommendations
      return {
        'data-cnstrc-recommendations': '',
        'data-cnstrc-recommendations-pod-id': this.responseTopProducts.response.pod.id,
        'data-cnstrc-result-id': this.responseTopProducts.result_id,
        'data-cnstrc-num-results': this.responseTopProducts.response.total_num_results
      };
    }
  },
  watch: {
    productsWithAttributes: {
      handler() {
        // For Firefox < 141
        if (!('CSS' in window && 'highlights' in CSS)) {
          return;
        }

        // If there isn't anything to highlight, bail
        if (!this.searchTerm.length || this.termsWithAttributes.length <= 0) {
          return;
        }

        const searchSuggestion = 'search-suggestion';
        CSS.highlights.delete(searchSuggestion);

        let options = [].concat(this.$refs.options);
        const ranges = options
          .map((item) => {
            let el = item.childNodes[0].childNodes[0];
            return { el, text: el.textContent.toLowerCase() };
          })
          .map(({text, el}) => {
            const indices = [];
            let startPos = 0;
            while (startPos < text.length) {
              const index = text.indexOf(this.searchTerm, startPos);

              if (index === -1) {
                break;
              }

              indices.push(index);
              startPos = index + this.searchTerm.length;
            }

            return indices.map((index) => {
              const range = new Range();
              range.setStart(el, index);
              range.setEnd(el, index + this.searchTerm.length);
              return range;
            });
          });

        const searchTermHighlight = new Highlight(...ranges.flat());
        CSS.highlights.set(searchSuggestion, searchTermHighlight);
      },
      flush: 'post'
    }
  },
  methods: {
    getHeading() {
      return window.search.heading;
    },
    getConfig() {
      return window.search.config;
    },
    getRecentlySearched() {
      // Don't try to shorten it to
      // JSON.parse(localStorage.getItem("recently_searched")).reverse()
      // even tho that will work locally.  Something about it breaks the
      // component when building for production without error.
      let recently_searched = JSON.parse(localStorage.getItem("recently_searched")) || [];
      return recently_searched.length ? recently_searched.reverse() : [];
    },
    getSuggests() {
      let config = this.getConfig(),
          sectionKey = this.isMobile() ? 'mobile' : 'desktop',
          length = config[sectionKey].search;

      return this.suggestions.slice(0, +length);
    },
    isMobile() {
      return window.matchMedia('(max-width: 767px)').matches;
    },
    getSearchSuggestionURL(suggestion) {
      let url = new URL(window.location.origin);

      url.pathname = '/catalogsearch/result/';
      url.searchParams.set('q', suggestion);

      return url.toString();
    },
    recentHandler(suggestion) {
      this.$emit('recentClick', suggestion);
    },
    trendingHandler(suggestion) {
      this.$emit('suggestionClick', suggestion);
    },
    productHandler(product, swatch) {
      let selectedSwatch = {},
        swatchOptions = product?.data?.swatch_options?.[206];

      if (!swatchOptions) {
        this.$emit('productClick');
        return;
      }

      let swatchId = Object.values(swatch)[0];

      if (swatchId) {
        selectedSwatch[product.data.id] = swatchId;
      } else {
        let swatchData = Object.values(swatchOptions)
          .filter((so) => so.label === product.data.color)
          .pop();

        // The products in the API response will sometimes be the parent product
        // and sometimes be the child products.  Parent products don't always
        // have "Color" set; so product.data.color will not be present on those.
        // When that happens the filter returns nothing.  This check is here to
        // prevent an error when trying to set the swatchData, M2PL-5345:
        // Cannot read properties of undefined (reading 'option_id')
        if (swatchData) {
          selectedSwatch[product.data.id] = swatchData.option_id;
        }
      }

      document.cookie = 'selected_swatch=' + encodeURIComponent(JSON.stringify(selectedSwatch)) + ";path=/";

      if (this.searchTerm) {
        this.$emit('productClick');
      }
    },
    noResultsLink() {
      let link = this.getSearchSuggestionURL(this.searchTerm);

      if (this.isSkuSearch(this.searchTerm)) {
        link = this.products[0].data.url;
      }

      return link;
    },
    isSkuSearch(searchTerm) {
      let found = this.products.find((product) => {
        return product.data.id.toLowerCase() === searchTerm.toLowerCase();
      });

      return !!found;
    }
  }
}
</script>

<style scoped>
::highlight(search-suggestion) {
  background-color: #FEEEB5;
  text-decoration: underline;
}

.autocomplete-dropdown {
  background: var(--color-white);
  left: 0;
  line-height: 1.2;
  position: fixed;
  margin-top: 15px;
  width: 100vw;
  z-index: 1000;
}

@media only screen and (max-width: 767px) {
  .open .autocomplete-dropdown {
    top: 74px; /* Magic number: 45px of text input 10px padding from container 19px padding from this */
    height: calc(100vh - 118px); /* Magic number from testing */
    overflow: scroll;
    margin-top: 0;
  }
}
@media only screen and (min-width: 768px) {
  .autocomplete-dropdown {
    border-top: solid 1px #cfcfcf;
  }
}

.autocomplete-wrapper {
  display: flex;
  flex-direction: column;
  gap: 25px;
  justify-content: flex-end;
  margin: 0 auto;
  max-width: 1280px;
}

@media only screen and (min-width: 768px) {
  .autocomplete-wrapper {
    flex-direction: row;
    padding: 35px 35px 35px 55px; /* Magic Number 55 is based on logo size */
  }
}

@media only screen and (min-width: 1024px) {
  div.autocomplete-wrapper {
    padding: 35px 35px 35px 165px; /* Magic Number 165 is based on logo size */
  }
}

@media only screen and (min-width: 768px) {
  .suggestion-container {
    flex: 1 0 25%;
    max-width: 325px;
  }
}

#products {
  flex: 1 1 auto;
}

.listbox {
  list-style: none;
  margin-bottom: 0;
  margin-top: 0;
  padding: 0;
}

.listbox li {
  margin-bottom: 0; /* Coming from somewhere in Magento */
}

.section-title,
.title {
  color: var(--color-black);
  flex: 1;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-bold);
  margin: 0;
  padding: 0 35px;
  text-align: left;
}

@media only screen and (min-width: 768px) {
  .section-title,
  .title {
    border-bottom: 0;
    padding: 0 35px 12px;
  }
}

.title.trending {
  margin-top: 20px;
}

@media only screen and (min-width: 768px) and (max-width: 1024px) {
  div#products ul.listbox,
  span.section-title {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  div#products ul.listbox {
    gap: 20px !important;
  }
}

.section-title {
  display: block;
}

@media only screen and (max-width: 767px) {
  .suggestion-container .title {
    border-bottom: 1px solid #b9b9b9;
    padding: 0 37px 12px;
  }
}

.listbox .option .suggestion {
  color: var(--color-dark-gray);
  cursor: pointer;
  display: flex;
  font-size: var(--font-size-small);
  justify-content: space-between;
  padding: 10px 35px 10px 43px;
  position: relative;
  text-decoration: none;
}

@media only screen and (min-width: 767px) {
  .listbox .option .suggestion {
    padding: 12px 35px;
  }
}

.listbox .option .suggestion:hover,
.listbox .option .suggestion:focus,
.listbox .option[aria-selected="true"] .suggestion {
  background-color: #D6D7D9;
}

.listbox .option .suggestion:focus-visible,
.listbox .option a:focus-visible,
[id="no-results-term"] a:focus-visible {
  outline: var(--brand-primary) auto 1px;
}

.recently_searched .recent .option .suggestion:before {
  background-image: url("data:image/svg+xml,%0A%3Csvg width='18' height='18' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='9' cy='9' r='8.5' stroke='%23797979'/%3E%3Cpath d='M9 3v7h6' stroke='%23797979' stroke-linejoin='bevel'/%3E%3C/svg%3E");
  background-size: contain;
  content: '';
  display: block;
  height: 18px;
  left: 15px;
  margin-top: -9px;
  position: absolute;
  top: 50%;
  width: 18px;
}

@media only screen and (min-width: 767px) {
  .recently_searched .listbox .option .suggestion:before {
    left: 8px;
  }
}

#products .listbox {
  gap: 35px;
  padding: 0 35px;
}

@media only screen and (max-width: 767px) {
  #products .listbox {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);
    padding: 15px 35px 35px;
  }
}

@media only screen and (min-width: 768px) {
  #products .listbox {
    display: flex;
  }
}

#products .listbox .option {
  flex: 0 1 33.33%;
}

#products .listbox .option a {
  color: var(--color-dark-gray);
  display: flex;
  flex-direction: column;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-normal);
  letter-spacing: 0.2px;
  line-height: 1.3;
  text-align: left;
  text-decoration: none;
}

[id="no-results-term"] {
  color: var(--color-dark-gray);
  padding: 0 35px;
  word-break: break-word;
}

[id="no-results-term"] p {
  margin: 0;
}

[id="no-results-term"] a {
  color: var(--color-dark-gray);
  text-decoration: none;
}

#no-results-term strong {
  color: var(--color-black);
}
</style>
